import { Link, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Music, Users, Calendar, Star, ArrowRight, Radio, MessageSquare } from 'lucide-react';

export default function Landing() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  // Redirect authenticated users to the app
  useEffect(() => {
    if (!loading && user) {
      navigate('/home', { replace: true });
    }
  }, [user, loading, navigate]);

  if (loading) return null;

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">

      {/* ── Navbar ───────────────────────────────────────────────── */}
      <nav className="sticky top-0 z-50 border-b border-border bg-background/80 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Radio className="w-6 h-6 text-primary" />
            <span className="text-xl font-bold tracking-tight">Resuena</span>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" asChild>
              <Link to="/login">Iniciar sesión</Link>
            </Button>
            <Button asChild>
              <Link to="/register">Crear cuenta</Link>
            </Button>
          </div>
        </div>
      </nav>


      {/* ── Hero ─────────────────────────────────────────────────── */}
      <section className="flex-1 flex flex-col items-center justify-center text-center px-4 py-20 sm:py-32">
        <div className="max-w-3xl mx-auto space-y-6">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary text-sm font-medium px-4 py-1.5 rounded-full border border-primary/20">
            <Music className="w-4 h-4" />
            La plataforma de músicos para músicos
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight">
            Conectá. Creá.{' '}
            <span className="text-primary">Resuena.</span>
          </h1>

          <p className="text-lg sm:text-xl text-muted-foreground max-w-xl mx-auto leading-relaxed">
            Encontrá colaboradores, conseguí fechas, mostrá tu portfolio y gestioná
            proyectos musicales — todo en un solo lugar.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
            <Button size="lg" asChild className="gap-2 text-base px-8">
              <Link to="/register">
                Empezar gratis <ArrowRight className="w-4 h-4" />
              </Link>
            </Button>
            <Button size="lg" variant="outline" asChild className="text-base px-8">
              <Link to="/login">Ya tengo cuenta</Link>
            </Button>
          </div>

          <p className="text-xs text-muted-foreground">
            Sin tarjeta de crédito · Siempre gratis en el plan básico
          </p>
        </div>
      </section>


      {/* ── Features ─────────────────────────────────────────────── */}
      <section className="bg-muted/40 border-y border-border py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-12">
            Todo lo que necesitás para tu carrera musical
          </h2>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: <Users className="w-7 h-7 text-primary" />,
                title: 'Matching de músicos',
                desc: 'Encontrá el guitarrista, productor o bajista ideal para tu proyecto con filtros por género, instrumento y ciudad.',
              },
              {
                icon: <Calendar className="w-7 h-7 text-primary" />,
                title: 'Marketplace de gigs',
                desc: 'Publicá y conseguí fechas en locales, eventos privados y festivales. También reservá espacios de ensayo y estudio.',
              },
              {
                icon: <Star className="w-7 h-7 text-primary" />,
                title: 'Portfolio digital',
                desc: 'Subí tus tracks, creá playlists y compartí tu trabajo. Tu perfil profesional disponible 24/7.',
              },
              {
                icon: <MessageSquare className="w-7 h-7 text-primary" />,
                title: 'Colaboración integrada',
                desc: 'Mensajería, gestión de proyectos y seguimiento de tareas para que tus colaboraciones avancen sin fricciones.',
              },
            ].map((f) => (
              <div
                key={f.title}
                className="bg-card rounded-xl border border-border p-6 space-y-3 hover:shadow-md transition-shadow"
              >
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                  {f.icon}
                </div>
                <h3 className="font-semibold text-base">{f.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* ── Bottom CTA ───────────────────────────────────────────── */}
      <section className="py-20 px-4 text-center">
        <div className="max-w-xl mx-auto space-y-6">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">
            ¿Listo para sonar diferente?
          </h2>
          <p className="text-muted-foreground text-lg">
            Unite a la comunidad de músicos que ya están conectando y creando en Resuena.
          </p>
          <Button size="lg" asChild className="gap-2 text-base px-10">
            <Link to="/register">
              Crear mi cuenta gratis <ArrowRight className="w-4 h-4" />
            </Link>
          </Button>
        </div>
      </section>

      {/* ── Footer ───────────────────────────────────────────────── */}
      <footer className="border-t border-border py-8 px-4">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <Radio className="w-4 h-4" />
            <span className="font-semibold text-foreground">Resuena</span>
            <span>© {new Date().getFullYear()}</span>
          </div>
          <div className="flex gap-6">
            <Link to="/register" className="hover:text-foreground transition-colors">Registrarse</Link>
            <Link to="/login" className="hover:text-foreground transition-colors">Iniciar sesión</Link>
          </div>
        </div>
      </footer>

    </div>
  );
}
